import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchBankLinkOptions } from '@/api/custom';
import { useAppSelector } from '@/store/hooks';
import { selectApexAccounts, selectApexLoading } from '@/store/features/apex/apex-selectors';
import {
  BankLinkReuseSource,
  BankLinkWallet,
  canSkipBankLinkOptionsFetch,
  isAttachAllWallet,
  isPickerManagedWallet,
} from '@/util/bank-link';

export type BankLinkWalletsState = {
  loading: boolean;
  pickerWallets: BankLinkWallet[];
  // Firm-data account rows carry no bank_link_mode, so this is the only way
  // they can tell an attach-all wallet apart.
  attachAllWalletIds: string[];
  pendingSources: BankLinkReuseSource[];
  loadError: boolean;
  reload: () => void;
};

const noopReload = (): void => {};

const emptyState: BankLinkWalletsState = {
  loading: false,
  pickerWallets: [],
  attachAllWalletIds: [],
  pendingSources: [],
  loadError: false,
  reload: noopReload,
};

const apexWalletKey = (
  accounts: readonly { id?: string; bank_link_mode?: string | null }[],
): string => accounts.map((account) => `${account.id ?? ''}:${account.bank_link_mode ?? ''}`).join('|');

export const useBankLinkWallets = (enabled = true): BankLinkWalletsState => {
  const apexAccounts = useAppSelector(selectApexAccounts);
  const apexLoading = useAppSelector(selectApexLoading);
  const canSkipFetch = canSkipBankLinkOptionsFetch(apexAccounts);
  const walletKey = useMemo(() => apexWalletKey(apexAccounts), [apexAccounts]);
  const [pickerWallets, setPickerWallets] = useState<BankLinkWallet[]>([]);
  const [attachAllWalletIds, setAttachAllWalletIds] = useState<string[]>([]);
  const [pendingSources, setPendingSources] = useState<BankLinkReuseSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => {
    setReloadKey((key) => key + 1);
  }, []);

  useEffect(() => {
    if (!enabled || apexLoading.accounts || canSkipFetch) {
      setPickerWallets([]);
      setAttachAllWalletIds([]);
      setPendingSources([]);
      setLoading(false);
      setLoadError(false);
      return;
    }

    setLoading(true);
    setLoadError(false);
    let cancelled = false;
    void fetchBankLinkOptions()
      .then((options) => {
        if (cancelled) {
          return;
        }
        setPickerWallets(options.wallets.filter(isPickerManagedWallet));
        setAttachAllWalletIds(
          options.wallets.filter(isAttachAllWallet).map((wallet) => wallet.account_id),
        );
        setPendingSources(options.pending_sources ?? []);
        setLoadError(false);
      })
      .catch((error) => {
        console.error('Failed to load bank link options', error);
        if (!cancelled) {
          setLoadError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, canSkipFetch, walletKey, apexLoading.accounts, reloadKey]);

  if (!enabled || apexLoading.accounts) {
    return emptyState;
  }

  return { loading, pickerWallets, attachAllWalletIds, pendingSources, loadError, reload };
};

export default useBankLinkWallets;
